/**
 * @component group-settings-page
 * @css group-settings-page.css
 * @primitives .btn.btn-primary, .btn.btn-secondary, .card
 */
import ProtectedComponent from './protected-component.js';
import logger from '../utils/logger.js';
import { renderPageTitle } from '../utils/emoji-badge.js';
import { formatThreadLabelHtml } from '../utils/group-threads.js';
import { escapeHtml } from '../utils/html-utils.js';
import { getProxyUrl } from '../utils/image-proxy-processor.js';
import { ADMIN_ROLES, MOD_ROLES } from '../utils/group-invite-policy.js';
import { readTabFromUrl, writeTabToUrl } from '../utils/tab-url.js';

const GROUP_SETTINGS_TAB_IDS = ['members', 'look', 'threads', 'reports', 'rss'];

class GroupSettingsPage extends ProtectedComponent {
  static get observedAttributes() {
    return ['group-id'];
  }

  constructor() {
    super();
    this.groupId = null;
    this.group = null;
    this.threads = [];
    this.activeTab = 'members';
    this.error = null;
    this.isSavingLook = false;
    this.sparkleTargetField = null;
    this._imageUrlHandler = null;
    this.openReportsCount = 0;
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.groupId = parseInt(this.getAttribute('group-id'), 10);
    if (this.currentUser && !Number.isNaN(this.groupId)) {
      this.load();
    }
  }

  connectedCallback() {
    super.connectedCallback();
    this.groupId = parseInt(this.getAttribute('group-id'), 10);
    this.activeTab = this.tabFromUrl() || 'members';
    this.load();
  }

  disconnectedCallback() {
    if (this._imageUrlHandler) {
      document.removeEventListener('image-url-selected', this._imageUrlHandler);
      this._imageUrlHandler = null;
    }
    super.disconnectedCallback();
  }

  setCurrentUser(user) {
    super.setCurrentUser(user);
    if (user && !Number.isNaN(this.groupId)) {
      this.load();
    }
  }

  tabFromUrl() {
    return readTabFromUrl(GROUP_SETTINGS_TAB_IDS);
  }

  isMod() {
    const role = this.group?.membership?.role;
    return role && MOD_ROLES.includes(role);
  }

  canBrand() {
    const role = this.group?.membership?.role;
    return role && ADMIN_ROLES.includes(role);
  }

  visibleTabs() {
    const tabs = [{ id: 'members', label: '👥 Members' }];
    tabs.push({ id: 'rss', label: '📡 RSS' });
    if (this.canBrand()) {
      tabs.push({ id: 'look', label: '🎨 Look' });
    }
    if (this.isMod()) {
      tabs.push({ id: 'threads', label: '🧵 Threads' });
      const reportsLabel = this.openReportsCount > 0
        ? `🚩 Reports (${this.openReportsCount})`
        : '🚩 Reports';
      tabs.push({ id: 'reports', label: reportsLabel });
    }
    return tabs;
  }

  defaultTab() {
    return 'members';
  }

  ensureValidTab() {
    const visible = this.visibleTabs().map((t) => t.id);
    if (!visible.includes(this.activeTab)) {
      this.activeTab = this.defaultTab();
    }
  }

  async load() {
    if (!window.API || !this.currentUser || Number.isNaN(this.groupId)) {
      return;
    }
    this.error = null;
    try {
      this.group = await window.API.request(`/groups/${this.groupId}`);
      const threadsUrl = this.isMod()
        ? `/groups/${this.groupId}/threads?include_archived=true`
        : `/groups/${this.groupId}/threads`;
      const threadsData = await window.API.request(threadsUrl);
      this.threads = threadsData.threads || [];
      if (this.isMod()) {
        await this.fetchOpenReportsCount();
      }
      this.ensureValidTab();
      const urlTab = this.tabFromUrl();
      if (urlTab && this.visibleTabs().some((t) => t.id === urlTab)) {
        this.activeTab = urlTab;
      }
    } catch (e) {
      this.error = e.message || 'Could not load group settings';
      logger.warn('GroupSettingsPage load failed', e);
    }
    this.render();
    this.attachListeners();
    if (this.activeTab === 'look') {
      await this.applyImagePreviews();
    }
    if (this.activeTab === 'members') {
      this.loadMembersPanel();
    }
    if (this.activeTab === 'reports') {
      this.loadModerationPanel();
    }
    if (this.activeTab === 'rss') {
      this.loadRssPanel();
    }
  }

  async fetchOpenReportsCount() {
    try {
      const data = await window.API.request(`/groups/${this.groupId}/reports?status=open`);
      this.openReportsCount = (data.reports || []).length;
    } catch {
      this.openReportsCount = 0;
    }
  }

  loadModerationPanel() {
    const panel = this.querySelector('group-settings-moderation');
    if (panel && this.group) {
      panel.setContext({
        groupId: this.groupId,
        group: this.group,
        membership: this.group.membership,
        currentUser: this.currentUser
      });
    }
  }

  loadMembersPanel() {
    const panel = this.querySelector('group-settings-members');
    if (panel && this.group) {
      panel.setContext({
        groupId: this.groupId,
        group: this.group,
        membership: this.group.membership,
        currentUser: this.currentUser
      });
    }
  }

  loadRssPanel() {
    const panel = this.querySelector('group-settings-rss');
    if (panel && this.group) {
      panel.setContext({
        groupId: this.groupId,
        group: this.group,
        membership: this.group.membership,
        threads: this.threads
      });
    }
  }

  renderTabs() {
    const tabs = this.visibleTabs();
    return tabs.map((t) => {
      const active = t.id === this.activeTab ? 'active' : '';
      return `<button type="button" class="group-settings-tab ${active}" data-tab="${t.id}">${t.label}</button>`;
    }).join('');
  }

  renderLookPanel() {
    if (!this.canBrand()) {
      return '';
    }
    const g = this.group || {};
    return `
      <div class="group-settings-tab-panel ${this.activeTab === 'look' ? 'active' : ''}" data-panel="look">
        <section class="settings-group">
          <div class="group-header">
            <h3>Group look</h3>
            <p class="group-description">Customize your group's banner accent, header image, and avatar.</p>
          </div>
          <form id="groupLookForm">
            <div class="settings-fields">
              <div class="settings-field-row">
                <div class="form-group">
                  <label class="form-label" for="bannerColorInput">Banner accent (hex)</label>
                  <p class="form-text">Accent color shown behind your group header</p>
                  <input
                    type="text"
                    class="form-control"
                    id="bannerColorInput"
                    name="header_banner_color"
                    maxlength="32"
                    placeholder="#ff6b9d"
                    value="${escapeHtml(g.header_banner_color || '')}"
                  />
                </div>
              </div>
              <div class="settings-field-row">
                <div class="form-group">
                  <label class="form-label" for="headerImageInput">Header image URL</label>
                  <p class="form-text">Wide banner image for the group page</p>
                  <div class="url-field-row">
                    <input
                      type="url"
                      class="form-control"
                      id="headerImageInput"
                      name="header_image_url"
                      value="${escapeHtml(g.header_image_url || '')}"
                      placeholder="https://… or Sparkle"
                    />
                    <button type="button" class="btn btn-secondary btn-sm sparkle-pick-btn" data-field="header_image_url">
                      ✨ Sparkle
                    </button>
                  </div>
                  <div class="image-preview" id="headerImagePreview"></div>
                </div>
              </div>
              <div class="settings-field-row">
                <div class="form-group">
                  <label class="form-label" for="avatarImageInput">Group avatar URL</label>
                  <p class="form-text">Square image shown next to the group name</p>
                  <div class="url-field-row">
                    <input
                      type="url"
                      class="form-control"
                      id="avatarImageInput"
                      name="avatar_image_url"
                      value="${escapeHtml(g.avatar_image_url || '')}"
                      placeholder="https://… or Sparkle"
                    />
                    <button type="button" class="btn btn-secondary btn-sm sparkle-pick-btn" data-field="avatar_image_url">
                      ✨ Sparkle
                    </button>
                  </div>
                  <div class="image-preview" id="avatarImagePreview"></div>
                </div>
              </div>
            </div>
            <div class="settings-panel-actions">
              <button type="submit" class="btn btn-primary" id="saveLookBtn">Save look</button>
            </div>
          </form>
        </section>
      </div>
    `;
  }

  isThreadArchived(thread) {
    return thread?.archived_at !== null && thread?.archived_at !== undefined && thread.archived_at !== '';
  }

  renderThreadRows() {
    return (this.threads || []).map((t) => `
      <div class="thread-row">
        <span>${this.isThreadArchived(t) ? formatThreadLabelHtml(t.name, { archived: true }) : formatThreadLabelHtml(t.name)}</span>
        <button
          type="button"
          class="btn btn-sm btn-secondary thread-archive-btn"
          data-thread-id="${t.id}"
          data-archived="${this.isThreadArchived(t) ? '1' : '0'}"
        >
          ${this.isThreadArchived(t) ? 'Unarchive' : 'Archive'}
        </button>
      </div>
    `).join('');
  }

  renderThreadsPanel() {
    if (!this.isMod()) {
      return '';
    }
    const rows = this.renderThreadRows();
    return `
      <div class="group-settings-tab-panel ${this.activeTab === 'threads' ? 'active' : ''}" data-panel="threads">
        <section class="settings-group">
          <div class="group-header">
            <h3>Threads</h3>
            <p class="group-description">Manage side conversations inside your group. Use <strong>Add thread</strong> on the group page to create new ones.</p>
          </div>
          <div class="thread-list">${rows || '<p class="empty-hint">No threads yet.</p>'}</div>
        </section>
      </div>
    `;
  }

  renderMembersPanel() {
    return `
      <div class="group-settings-tab-panel ${this.activeTab === 'members' ? 'active' : ''}" data-panel="members">
        <group-settings-members></group-settings-members>
      </div>
    `;
  }

  renderReportsPanel() {
    if (!this.isMod()) {
      return '';
    }
    return `
      <div class="group-settings-tab-panel ${this.activeTab === 'reports' ? 'active' : ''}" data-panel="reports">
        <group-settings-moderation></group-settings-moderation>
      </div>
    `;
  }

  renderRssPanel() {
    return `
      <div class="group-settings-tab-panel ${this.activeTab === 'rss' ? 'active' : ''}" data-panel="rss">
        <group-settings-rss></group-settings-rss>
      </div>
    `;
  }

  render() {
    if (Number.isNaN(this.groupId)) {
      this.innerHTML = '<p class="error">Invalid group.</p>';
      return;
    }
    if (this.error && !this.group) {
      this.innerHTML = `<p class="error">${escapeHtml(this.error)}</p>`;
      return;
    }
    if (!this.group) {
      this.innerHTML = `
        <div class="group-settings-page">
          <div class="loading-state">
            <div class="spinner"></div>
            <div>Loading settings…</div>
          </div>
        </div>
      `;
      return;
    }

    const groupName = escapeHtml(this.group.name || 'Group');

    this.innerHTML = `
      <div class="group-settings-page">
        <div class="settings-header">
          ${renderPageTitle('⚙️', `${groupName} Settings`, 'h1')}
          <p class="settings-subtitle">Manage your group's look, members, RSS feeds, threads, and reports</p>
        </div>

        <div class="group-settings-tabs" role="tablist">
          ${this.renderTabs()}
        </div>

        <div class="group-settings-panels">
          ${this.renderMembersPanel()}
          ${this.renderRssPanel()}
          ${this.renderLookPanel()}
          ${this.renderThreadsPanel()}
          ${this.renderReportsPanel()}
        </div>

        <div class="alert-container"></div>

        <footer class="group-settings-footer">
          <a href="/groups/${this.groupId}" class="btn btn-secondary" data-route="/groups/${this.groupId}">← Back to group</a>
        </footer>
      </div>
    `;
  }

  switchTab(tabId) {
    if (!this.visibleTabs().some((t) => t.id === tabId)) {
      return;
    }
    this.activeTab = tabId;
    writeTabToUrl(tabId);
    this.render();
    this.attachListeners();
    if (tabId === 'look') {
      this.applyImagePreviews();
    }
    if (tabId === 'members') {
      this.loadMembersPanel();
    }
    if (tabId === 'reports') {
      this.loadModerationPanel();
    }
    if (tabId === 'rss') {
      this.loadRssPanel();
    }
  }

  attachListeners() {
    this.querySelectorAll('.group-settings-tab').forEach((tab) => {
      tab.addEventListener('click', () => {
        this.switchTab(tab.getAttribute('data-tab'));
      });
    });

    this.addEventListener('group-settings-saved', (e) => {
      if (e.detail?.group) {
        this.group = e.detail.group;
      }
    });

    this.addEventListener('group-members-changed', () => {
      this.load();
    });

    this.addEventListener('group-reports-count', (e) => {
      this.openReportsCount = e.detail?.openCount ?? 0;
      this.querySelectorAll('.group-settings-tab[data-tab="reports"]').forEach((tab) => {
        tab.textContent = this.openReportsCount > 0
          ? `🚩 Reports (${this.openReportsCount})`
          : '🚩 Reports';
      });
    });

    this.addEventListener('group-moderation-alert', (e) => {
      const { message, type } = e.detail || {};
      if (message) {
        this.showAlert(message, type || 'info');
      }
    });

    const lookForm = this.querySelector('#groupLookForm');
    lookForm?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.saveLook();
    });

    this.querySelectorAll('.sparkle-pick-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.sparkleTargetField = btn.getAttribute('data-field');
        this.openSparklePicker();
      });
    });

    this.attachThreadArchiveListeners();

    if (!this._imageUrlHandler) {
      this._imageUrlHandler = (e) => {
        if (!this.sparkleTargetField || this.activeTab !== 'look') return;
        const input = this.querySelector(`[name="${this.sparkleTargetField}"]`);
        if (input && e.detail?.fileUrl) {
          input.value = e.detail.fileUrl;
          this.applyImagePreviews();
        }
        this.sparkleTargetField = null;
      };
      document.addEventListener('image-url-selected', this._imageUrlHandler);
    }
  }

  attachThreadArchiveListeners() {
    this.querySelectorAll('.thread-archive-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const threadId = parseInt(btn.getAttribute('data-thread-id'), 10);
        const archived = btn.getAttribute('data-archived') === '1';
        const thread = this.threads.find((t) => Number(t.id) === threadId);
        const previousArchivedAt = thread?.archived_at ?? null;
        if (thread) {
          thread.archived_at = archived ? null : new Date().toISOString();
          this.render();
          this.attachListeners();
        }
        try {
          await window.API.request(`/groups/${this.groupId}/threads/${threadId}`, {
            method: 'PUT',
            body: JSON.stringify({ archived: !archived })
          });
          const threadsUrl = this.isMod()
            ? `/groups/${this.groupId}/threads?include_archived=true`
            : `/groups/${this.groupId}/threads`;
          const data = await window.API.request(threadsUrl);
          this.threads = data.threads || [];
          this.render();
          this.attachListeners();
          this.dispatchEvent(new CustomEvent('group-threads-changed', { bubbles: true }));
        } catch (error) {
          if (thread) {
            thread.archived_at = previousArchivedAt;
            this.render();
            this.attachListeners();
          }
          this.showAlert(error.message || 'Thread update failed', 'error');
        }
      });
    });
  }

  openSparklePicker() {
    let picker = document.querySelector('media-source-picker[output-mode="url"]');
    if (!picker) {
      picker = document.createElement('media-source-picker');
      picker.setAttribute('selection-mode', 'single');
      picker.setAttribute('output-mode', 'url');
      document.body.appendChild(picker);
    }
    picker.open();
  }

  async applyImagePreviews() {
    const uid = this.currentUser?.id;
    if (!uid) return;

    const headerInput = this.querySelector('[name="header_image_url"]');
    const avatarInput = this.querySelector('[name="avatar_image_url"]');
    const headerPreview = this.querySelector('#headerImagePreview');
    const avatarPreview = this.querySelector('#avatarImagePreview');

    if (headerPreview && headerInput?.value?.trim()) {
      const url = await getProxyUrl(headerInput.value.trim(), uid, null);
      headerPreview.innerHTML = url
        ? `<img src="${escapeHtml(url)}" alt="" class="preview-thumb banner-preview" />`
        : '';
    } else if (headerPreview) {
      headerPreview.innerHTML = '';
    }

    if (avatarPreview && avatarInput?.value?.trim()) {
      const url = await getProxyUrl(avatarInput.value.trim(), uid, null);
      avatarPreview.innerHTML = url
        ? `<img src="${escapeHtml(url)}" alt="" class="preview-thumb avatar-preview" />`
        : '';
    } else if (avatarPreview) {
      avatarPreview.innerHTML = '';
    }
  }

  async saveLook() {
    if (this.isSavingLook || !this.group) return;
    const form = this.querySelector('#groupLookForm');
    if (!form) return;

    this.isSavingLook = true;
    try {
      const body = {
        header_banner_color: form.querySelector('[name="header_banner_color"]')?.value?.trim() || null,
        header_image_url: form.querySelector('[name="header_image_url"]')?.value?.trim() || null,
        avatar_image_url: form.querySelector('[name="avatar_image_url"]')?.value?.trim() || null
      };
      await window.API.request(`/groups/${this.groupId}`, {
        method: 'PUT',
        body: JSON.stringify(body)
      });
      this.group = await window.API.request(`/groups/${this.groupId}`);
      this.dispatchEvent(new CustomEvent('group-settings-saved', {
        detail: { group: this.group },
        bubbles: true
      }));
      this.showAlert('Group look saved!', 'success');
      await this.applyImagePreviews();
    } catch (error) {
      this.showAlert(error.message || 'Could not save', 'error');
    } finally {
      this.isSavingLook = false;
    }
  }

  showAlert(message, type = 'info') {
    const container = this.querySelector('.alert-container');
    if (!container) return;
    container.innerHTML = `<div class="alert alert-${type}">${escapeHtml(message)}</div>`;
    setTimeout(() => {
      if (container) container.innerHTML = '';
    }, 4000);
  }
}

customElements.define('group-settings-page', GroupSettingsPage);
export default GroupSettingsPage;
